// useBreakpoint.js
// Single source of truth for the app's responsive tiers — the whole continuum
// from phone to a 32" monitor. Drives the AppShell nav chrome and lets any
// screen gate features/columns by device class.
//
// Tiers:
//   phone    < 768px       → mobile shell (bottom tab bar + More sheet), 1 col
//   tablet   768–1199px    → icon rail nav, denser touch layout, ~2 col
//   desktop  1200–1919px   → full sidebar, tables / multi-column
//   wide     >= 1920px     → full sidebar; content fills the width — extra space
//                            becomes denser grids / bigger charts, not margins.
import { useEffect, useState } from "react";

export const BP = { PHONE_MAX: 767, TABLET_MAX: 1199, DESKTOP_MAX: 1919 };

function resolve(width) {
  if (width <= BP.PHONE_MAX) return "phone";
  if (width <= BP.TABLET_MAX) return "tablet";
  if (width <= BP.DESKTOP_MAX) return "desktop";
  return "wide";
}

function initialWidth() {
  if (typeof window === "undefined") return 1280; // SSR / test fallback → desktop
  return window.innerWidth;
}

export function useBreakpoint() {
  const [bp, setBp] = useState(() => resolve(initialWidth()));

  useEffect(() => {
    // One resize listener, cheap: only commit state when the tier actually changes.
    const onResize = () => {
      const next = resolve(window.innerWidth);
      setBp((prev) => (prev === next ? prev : next));
    };
    onResize();
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return {
    bp,
    isPhone: bp === "phone",
    isTablet: bp === "tablet",
    isDesktop: bp === "desktop",
    isWide: bp === "wide",
    // Convenience groupings for shared branches.
    isCompact: bp === "phone" || bp === "tablet", // narrow layouts
    hasSidebar: bp === "desktop" || bp === "wide", // full labelled sidebar tiers
  };
}

export default useBreakpoint;
